/**
 * Live Results Page (Public/Spectator View)
 * Real-time race results for spectators
 */

import { getTimestamps, formatTime } from '../lib/timing.js'
import { getAthleteByBib, getSelectedRace, loadRoster } from '../lib/storage.js'
import { getBibEntries } from './chute.js'
import { getAthleteName } from '../lib/csv.js'
import { subscribeToTimestamps, subscribeToBibEntries, getConnectionStatus } from '../lib/firebase.js'

// State
let currentView = 'overall' // 'overall', 'male', 'female'
let autoRefresh = true
let timestampSubscription = null
let bibSubscription = null
let refreshInterval = null
const REFRESH_INTERVAL_MS = 3000 // Fallback polling interval

/**
 * Generate shareable URL for results
 * Points to the public results site (www.manarunning.com)
 */
function generateShareUrl(race) {
  const raceId = race?.id || ''
  return `https://www.manarunning.com/results${raceId ? `?race=${raceId}` : ''}`
}

/**
 * Copy text to clipboard and show feedback
 */
async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text)
    showToast('Link copied to clipboard!')
  } catch (err) {
    // Fallback for older browsers
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
    showToast('Link copied to clipboard!')
  }
}

/**
 * Show a toast notification
 */
function showToast(message) {
  const existing = document.querySelector('.toast')
  if (existing) existing.remove()

  const toast = document.createElement('div')
  toast.className = 'toast'
  toast.textContent = message
  document.body.appendChild(toast)

  setTimeout(() => {
    toast.classList.add('show')
  }, 10)

  setTimeout(() => {
    toast.classList.remove('show')
    setTimeout(() => toast.remove(), 300)
  }, 2000)
}

/**
 * Get live results data
 */
function getLiveResults() {
  const timestamps = getTimestamps()
  const bibEntries = getBibEntries()
  const results = []

  const maxLen = Math.max(timestamps.length, bibEntries.length)

  for (let i = 0; i < maxLen; i++) {
    const timestamp = timestamps[i] || null
    const entry = bibEntries[i] || null
    const athlete = entry?.bib ? getAthleteByBib(entry.bib) : null

    if (timestamp && entry && !entry.skipped) {
      results.push({
        position: i + 1,
        bib: entry.bib,
        unknown: entry.unknown || false,
        time: timestamp.netTime,
        athlete,
        name: athlete ? getAthleteName(athlete) : (entry.unknown ? 'Pending ID' : `Bib #${entry.bib}`),
        gender: athlete?.gender || null,
        team: athlete?.team || null,
        ageGroup: athlete?.ageGroup || null
      })
    }
  }

  // Sort by time
  results.sort((a, b) => a.time - b.time)

  // Calculate ranks
  results.forEach((r, idx) => {
    r.overallRank = idx + 1
  })

  // Gender ranks
  const maleResults = results.filter(r => r.gender === 'M')
  const femaleResults = results.filter(r => r.gender === 'F')

  maleResults.forEach((r, idx) => { r.genderRank = idx + 1 })
  femaleResults.forEach((r, idx) => { r.genderRank = idx + 1 })

  return results
}

/**
 * Filter results by view
 */
function filterByView(results, view) {
  if (view === 'male') return results.filter(r => r.gender === 'M')
  if (view === 'female') return results.filter(r => r.gender === 'F')
  return results
}

/**
 * Render the Live Results page
 */
export function renderLiveResultsPage(container, onNavigate) {
  // Initial container setup
  if (!container.innerHTML || !container.querySelector('.live-results-page')) {
    initializeUpdates(container, onNavigate)
  }

  const race = getSelectedRace()
  const allResults = getLiveResults()
  const filteredResults = filterByView(allResults, currentView)
  const roster = loadRoster()

  const stats = {
    total: allResults.length,
    male: allResults.filter(r => r.gender === 'M').length,
    female: allResults.filter(r => r.gender === 'F').length,
    pending: allResults.filter(r => r.unknown).length,
    registered: roster.length
  }

  container.innerHTML = `
    <div class="live-results-page">
      <header class="live-header">
        <div class="live-header-top">
          <div class="live-badge ${getConnectionStatus() === 'connected' ? 'connected' : ''}">
            <span class="live-dot"></span>
            LIVE
          </div>
          <h1 class="race-title">${race?.name || 'Race Results'}</h1>
          ${race?.distanceValue ? `<span class="race-distance">${race.distanceValue} ${race.distanceUnit === 'kilometers' ? 'km' : 'mi'}</span>` : ''}
          <div class="share-actions">
            <button class="btn-share" id="shareBtn" title="Share results">
              Share Link
            </button>
            <button class="btn-print" id="printBtn" title="Print results">
              Print
            </button>
          </div>
        </div>
        <div class="live-stats">
          <span class="stat">${stats.total} Finishers</span>
          <span class="stat-divider">|</span>
          <span class="stat">${stats.registered} Registered</span>
          ${stats.pending > 0 ? `<span class="stat pending">${stats.pending} Pending ID</span>` : ''}
        </div>
      </header>

      <div class="view-tabs">
        <button class="view-tab ${currentView === 'overall' ? 'active' : ''}" data-view="overall">
          Overall
        </button>
        <button class="view-tab ${currentView === 'male' ? 'active' : ''}" data-view="male">
          Male (${stats.male})
        </button>
        <button class="view-tab ${currentView === 'female' ? 'active' : ''}" data-view="female">
          Female (${stats.female})
        </button>
      </div>

      <div class="results-container">
        ${filteredResults.length > 0 ? `
          <div class="results-list">
            ${filteredResults.slice(0, 100).map((r, idx) => `
              <div class="result-row ${idx < 3 ? 'podium podium-' + (idx + 1) : ''} ${r.unknown ? 'pending' : ''}">
                <div class="rank">${currentView === 'overall' ? r.overallRank : r.genderRank}</div>
                <div class="athlete-info">
                  <div class="athlete-name">${r.name}</div>
                  <div class="athlete-meta">
                    ${r.unknown ? '' : `<span class="bib">#${r.bib}</span>`}
                    ${r.team ? `<span class="team">${r.team}</span>` : ''}
                    ${r.gender ? `<span class="gender">${r.gender}</span>` : ''}
                  </div>
                </div>
                <div class="time">${formatTime(r.time)}</div>
              </div>
            `).join('')}
          </div>
          ${filteredResults.length > 100 ? `
            <div class="more-results">
              Showing top 100 of ${filteredResults.length} finishers
            </div>
          ` : ''}
        ` : `
          <div class="no-results">
            <div class="waiting-icon">⏱</div>
            <p>Waiting for finishers...</p>
            <p class="sub">Results will appear here as runners cross the finish line</p>
          </div>
        `}
      </div>

      <footer class="live-footer">
        <div class="refresh-indicator ${autoRefresh ? 'active' : ''}">
          <span class="refresh-dot"></span>
          Auto-refresh ${autoRefresh ? 'ON' : 'OFF'}
        </div>
        <div class="powered-by">
          Powered by <strong>ManaTiming</strong>
        </div>
      </footer>
    </div>
  `

  attachLiveResultsHandlers(container, onNavigate)
}

/**
 * Attach event handlers
 */
function attachLiveResultsHandlers(container, onNavigate) {
  // View tabs
  container.querySelectorAll('.view-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      currentView = tab.dataset.view
      renderLiveResultsPage(container, onNavigate)
    })
  })

  // Toggle auto-refresh on click
  container.querySelector('.refresh-indicator')?.addEventListener('click', () => {
    // Legacy toggle, maybe keep for UI but it doesn't do much with real-time
    autoRefresh = !autoRefresh
    renderLiveResultsPage(container, onNavigate)
  })

  // Share button
  document.getElementById('shareBtn')?.addEventListener('click', () => {
    const race = getSelectedRace()
    const shareUrl = generateShareUrl(race)
    copyToClipboard(shareUrl)
  })

  // Print button
  document.getElementById('printBtn')?.addEventListener('click', () => {
    window.print()
  })
}

/**
 * Render initialization - set up real-time subscriptions and fallback polling
 */
function initializeUpdates(container, onNavigate) {
  const race = getSelectedRace()

  // Clean up any existing subscriptions
  cleanupSubscriptions()

  const handleUpdate = () => {
    // Only refresh if we're still on the live results page
    if (container.querySelector('.live-results-page')) {
      renderLiveResultsPage(container, onNavigate)
    }
  }

  // Subscribe to real-time updates if we have a race
  if (race?.id) {
    subscribeToLiveUpdates(race.id, handleUpdate)
  }

  // Also set up fallback polling in case real-time fails
  if (autoRefresh) {
    refreshInterval = setInterval(handleUpdate, REFRESH_INTERVAL_MS)
  }
}

/**
 * Clean up subscriptions when leaving the page
 */
function cleanupSubscriptions() {
  if (timestampSubscription) {
    timestampSubscription()
    timestampSubscription = null
  }
  if (bibSubscription) {
    bibSubscription()
    bibSubscription = null
  }
  if (refreshInterval) {
    clearInterval(refreshInterval)
    refreshInterval = null
  }
}

/**
 * Subscribe to real-time updates from Firebase
 */
export function subscribeToLiveUpdates(raceId, onUpdate) {
  // Subscribe to timestamps
  timestampSubscription = subscribeToTimestamps(raceId, (timestamps) => {
    console.log('Real-time timestamp update:', timestamps?.length || 0, 'items')
    onUpdate()
  })

  // Subscribe to bib entries
  bibSubscription = subscribeToBibEntries(raceId, (entries) => {
    console.log('Real-time bib update:', entries?.length || 0, 'items')
    onUpdate()
  })

  return { timestampSubscription, bibSubscription }
}

/**
 * Export cleanup function for use when navigating away
 */
export function cleanupLiveResults() {
  cleanupSubscriptions()
}

